import { useRef, useState, type FormEvent } from 'react';
import { haptic, hapticResult } from '../lib/telegram.js';

interface Props {
  onSend: (text: string) => Promise<void>;
  onOpenMeals: () => void;
  onOpenScanner: () => void;
  busy: boolean;
}

/**
 * Строка ввода еды.
 *
 * Слева - кнопка «+»: открывает сохранённые блюда. Кнопка отправки не занимает
 * место, пока писать нечего: с первым символом она «выезжает» из правого края
 * поля, а поле подбирается под неё.
 */
export function Composer({ onSend, onOpenMeals, onOpenScanner, busy }: Props) {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const filled = text.trim().length > 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const value = text.trim();
    if (!value || busy) return;

    haptic('medium');
    // Поле чистится сразу: запись уже появилась в ленте со статусом, и
    // держать текст до ответа сервера незачем - выглядит так, будто отправка
    // не прошла. Клавиатура остаётся открытой: обычно пишут несколько
    // позиций подряд.
    setText('');
    inputRef.current?.focus();

    try {
      await onSend(value);
    } catch {
      hapticResult('error');
      // Возвращаем текст, только если человек не начал печатать следующее -
      // иначе затёрли бы то, что он уже набрал
      setText((current) => (current ? current : value));
    }
  }

  return (
    <form className={`composer${filled ? ' filled' : ''}`} onSubmit={submit}>
      <button
        type="button"
        className="composer-plus glass"
        onClick={() => {
          haptic('light');
          onOpenMeals();
        }}
        aria-label="Мои блюда"
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 5v14M5 12h14"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <div className="composer-field glass">
        <input
          ref={inputRef}
          className="composer-input"
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Что вы съели?"
          enterKeyHint="send"
          autoComplete="off"
          disabled={busy}
          aria-label="Добавить еду в дневник"
        />
        <button
          className="composer-camera"
          type="button"
          onClick={() => {
            haptic('light');
            onOpenScanner();
          }}
          disabled={busy}
          tabIndex={filled ? -1 : 0}
          aria-hidden={filled}
          aria-label="Сканировать штрихкод"
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M8.2 6.5 9.4 4.8h5.2l1.2 1.7h2.7A2.5 2.5 0 0 1 21 9v8.5a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17.5V9a2.5 2.5 0 0 1 2.5-2.5h2.7Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="13" r="3.2" stroke="currentColor" strokeWidth="1.8" />
          </svg>
        </button>
      </div>

      <button
        className="composer-send"
        type="submit"
        disabled={!filled || busy}
        tabIndex={filled ? 0 : -1}
        aria-hidden={!filled}
        aria-label="Добавить"
      >
        {busy ? (
          <span className="composer-spinner" />
        ) : (
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4.3 11.4 19.1 5.2c.9-.4 1.8.5 1.4 1.4l-6.2 14.8c-.4.9-1.7.9-2-.1l-1.8-5.3a1.1 1.1 0 0 0-.7-.7l-5.3-1.8c-1-.3-1-1.6-.2-2Z"
              fill="currentColor"
            />
          </svg>
        )}
      </button>
    </form>
  );
}
